import { Extension } from '@tiptap/core';
import { PluginKey } from '@tiptap/pm/state';
import {
  Suggestion,
  type SuggestionMatch,
  type SuggestionProps,
  type Trigger,
} from '@tiptap/suggestion';
import type { TextBuildingBlock } from 'types/textBuildingBlocks';
import { templateToContent } from './buildingBlockContent';

export const templateSuggestionPluginKey = new PluginKey('templateSuggestion');

const TRIGGER = 't.';

// Custom matcher: Suggestion's default builds a character class that excludes the trigger's own
// characters ("t", ".") from the query, which breaks a two-character trigger. This reads the
// block-local text before the caret via $position — no whole-doc plain-text/offset round-trip — and
// takes everything after "t." up to whitespace as the query.
export function findTemplateTrigger({ $position }: Trigger): SuggestionMatch {
  const blockStart = $position.start();
  // 1-char leaf text keeps string indices aligned with document offsets across inline atoms.
  const before = $position.parent.textBetween(0, $position.parentOffset, undefined, '￼');
  const start = before.lastIndexOf(TRIGGER);
  if (start === -1) return null;

  // The "t" must sit at a word boundary: block start or preceded by whitespace.
  const prev = start === 0 ? '' : before[start - 1];
  if (prev !== '' && !/\s/.test(prev)) return null;

  const query = before.slice(start + TRIGGER.length);
  // A whitespace character between "t." and the caret closes the trigger.
  if (/\s/.test(query)) return null;

  return {
    range: { from: blockStart + start, to: $position.pos },
    query,
    text: before.slice(start),
  };
}

export type TemplateSuggestionProps = SuggestionProps<TextBuildingBlock, TextBuildingBlock>;

export interface TemplateSuggestionBridge {
  items: (query: string) => TextBuildingBlock[];
  onStart: (props: TemplateSuggestionProps) => void;
  onUpdate: (props: TemplateSuggestionProps) => void;
  onKeyDown: (event: KeyboardEvent) => boolean;
  onExit: () => void;
  onInserted: (fromPos: number) => void;
}

export interface TemplateSuggestionOptions {
  getBridge: () => TemplateSuggestionBridge | null;
}

export const TemplateSuggestion = Extension.create<TemplateSuggestionOptions>({
  name: 'templateSuggestion',

  addOptions() {
    return { getBridge: () => null };
  },

  addProseMirrorPlugins() {
    const getBridge = this.options.getBridge;
    return [
      Suggestion<TextBuildingBlock, TextBuildingBlock>({
        editor: this.editor,
        pluginKey: templateSuggestionPluginKey,
        char: TRIGGER,
        findSuggestionMatch: findTemplateTrigger,
        items: ({ query }) => getBridge()?.items(query) ?? [],
        command: ({ editor, range, props }) => {
          editor.chain().focus().insertContentAt(range, templateToContent(props.text)).run();
          getBridge()?.onInserted(range.from);
        },
        render: () => ({
          onStart: (props) => getBridge()?.onStart(props),
          onUpdate: (props) => getBridge()?.onUpdate(props),
          onKeyDown: ({ event }) => getBridge()?.onKeyDown(event) ?? false,
          onExit: () => getBridge()?.onExit(),
        }),
      }),
    ];
  },
});
